import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { theme } from './theme/theme';
import { useAuth } from './hooks/useAuth';

// ── Lazy pages ────────────────────────────────────────────────────────────────

const LandingPage     = lazy(() => import('./pages/landing/LandingPage'));
const LoginPage       = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage    = lazy(() => import('./pages/auth/RegisterPage'));
const DashboardPage   = lazy(() => import('./pages/dashboard/DashboardPage'));
const DeployPage      = lazy(() => import('./pages/deploy/DeployPage'));
const MarketplacePage   = lazy(() => import('./pages/marketplace/MarketplacePage'));
const PluginDetailPage  = lazy(() => import('./pages/marketplace/PluginDetailPage'));
const BillingPage     = lazy(() => import('./pages/billing/BillingPage'));
const SettingsPage    = lazy(() => import('./pages/settings/SettingsPage'));

// ── Product deep-dive pages (public) ──────────────────────────────────────────

const HermesPage = lazy(() => import('./pages/products/HermesPage'));
const ArgusPage  = lazy(() => import('./pages/products/ArgusPage'));
const AtlasPage  = lazy(() => import('./pages/products/AtlasPage'));

// ── Layouts ───────────────────────────────────────────────────────────────────

import AppShell from './components/AppShell';
import MarketplaceShell from './components/MarketplaceShell';

// ── Guards ────────────────────────────────────────────────────────────────────

function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <FullPageSpinner />;
  if (!isAuthenticated) return <Navigate to="/auth/login" replace />;
  return <Outlet />;
}

function RequireGuest() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <FullPageSpinner />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function FullPageSpinner() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
}

// ── Router ────────────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        {/* Public landing */}
        <Route path="/" element={<LandingPage />} />

        {/* Product deep-dive pages */}
        <Route path="/products/hermes" element={<HermesPage />} />
        <Route path="/products/argus"  element={<ArgusPage />} />
        <Route path="/products/atlas"  element={<AtlasPage />} />

        {/* Auth (guest-only) */}
        <Route element={<RequireGuest />}>
          <Route path="/auth/login"    element={<LoginPage />} />
          <Route path="/auth/register" element={<RegisterPage />} />
        </Route>

        {/* Marketplace — público, layout adaptativo (sidebar se logado, navbar se anônimo) */}
        <Route element={<MarketplaceShell />}>
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/marketplace/:product/:name" element={<PluginDetailPage />} />
        </Route>

        {/* App protegido */}
        <Route element={<RequireAuth />}>
          <Route element={<AppShell />}>
            <Route path="/dashboard"     element={<DashboardPage />} />
            <Route path="/deploy"        element={<DeployPage />} />
            <Route path="/billing"       element={<BillingPage />} />
            <Route path="/settings"      element={<SettingsPage />} />
            <Route path="/settings/:tab" element={<SettingsPage />} />
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </ThemeProvider>
  );
}
